import React, { useEffect, useState } from "react";
import { DashboardShell } from "@/pages/Dashboard";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RouteForm } from "@/components/RouteForm";
import { CARGO_LABELS, STATUS_LABELS, formatEUR, formatDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Route as RouteIcon, Inbox, Trash2, BadgeCheck, ShieldCheck, MapPin, ArrowRight, Split, Leaf } from "lucide-react";
import { formatCo2 } from "@/lib/constants";

const statusColor = { pending: "bg-amber-500", accepted: "bg-emerald-600", rejected: "bg-destructive", completed: "bg-primary", active: "bg-emerald-600", cancelled: "bg-muted-foreground" };

export default function CarrierDashboard() {
  const { user } = useAuth();
  const [routes, setRoutes] = useState([]);
  const [received, setReceived] = useState([]);
  const [open, setOpen] = useState(false);

  const load = () => {
    api.get("/routes/mine").then(({ data }) => setRoutes(data)).catch(() => {});
    api.get("/bookings/received").then(({ data }) => setReceived(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const stats = [
    { label: "Tratte attive", value: routes.filter((r) => r.status === "active").length, icon: RouteIcon },
    { label: "Richieste totali", value: received.length, icon: Inbox },
    { label: "In attesa", value: received.filter((b) => b.status === "pending").length, icon: ShieldCheck },
    { label: "CO₂ risparmiata", value: formatCo2(routes.reduce((s, r) => s + (r.co2_saved_kg || 0), 0)), icon: Leaf, eco: true },
  ];

  const setStatus = async (id, status) => {
    try { await api.put(`/bookings/${id}/status`, { status }); toast.success("Aggiornato"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const delRoute = async (id) => {
    try { await api.delete(`/routes/${id}`); toast.success("Tratta eliminata"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <DashboardShell title="Dispatch board" subtitle={user?.company || "Gestisci tratte e richieste in arrivo."}
      actions={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-sm bg-accent hover:bg-accent/90 text-accent-foreground font-semibold gap-2" data-testid="add-route-btn"><Plus size={16} /> Inserisci tratta vuota</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-sm">
            <DialogHeader><DialogTitle className="font-display">Nuova tratta a vuoto</DialogTitle></DialogHeader>
            <RouteForm onCreated={() => { setOpen(false); load(); }} />
          </DialogContent>
        </Dialog>
      }>

      {!user?.verified && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-sm p-4 mb-6 text-sm flex items-center gap-2" data-testid="verify-banner">
          <ShieldCheck size={16} className="text-amber-600" /> Il tuo account trasportatore è in attesa di verifica da parte dell'amministrazione.
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {stats.map((s) => (
          <div key={s.label} className={`border rounded-sm p-4 ${s.eco ? "border-emerald-200 bg-emerald-50" : "border-border bg-card"}`}>
            <div className="flex items-center justify-between">
              <span className="label-eyebrow text-muted-foreground">{s.label}</span>
              <s.icon size={16} className={s.eco ? "text-emerald-600" : "text-primary"} />
            </div>
            <p className={`font-display font-extrabold text-3xl mt-2 ${s.eco ? "text-emerald-700" : ""}`}>{s.value}</p>
          </div>
        ))}
      </div>

      <Tabs defaultValue="routes">
        <TabsList className="rounded-sm">
          <TabsTrigger value="routes" data-testid="tab-my-routes">Le mie tratte</TabsTrigger>
          <TabsTrigger value="requests" data-testid="tab-requests">Richieste in arrivo</TabsTrigger>
        </TabsList>

        <TabsContent value="routes" className="mt-6 space-y-3">
          {routes.length === 0 && <p className="text-sm text-muted-foreground">Nessuna tratta pubblicata.</p>}
          {routes.map((r) => (
            <div key={r.route_id} className="border border-border rounded-sm p-4 bg-card flex items-center justify-between gap-4" data-testid={`my-route-${r.route_id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 font-display font-semibold">
                  <MapPin size={15} className="text-primary" /> {r.origin} <ArrowRight size={14} className="text-accent" /> {r.destination}
                  {r.partial_available && <Split size={14} className="text-accent" />}
                </div>
                <p className="text-xs text-muted-foreground mt-1">{formatDate(r.departure_date)} · {r.cargo_types?.map((c) => CARGO_LABELS[c]).join(", ")}</p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="font-display font-bold text-primary text-sm">{formatEUR(r.price_min)}</span>
                <Badge className={`rounded-sm text-white ${statusColor[r.status]}`}>{STATUS_LABELS[r.status]}</Badge>
                <Button variant="ghost" size="icon" className="rounded-sm text-destructive hover:bg-destructive/10" onClick={() => delRoute(r.route_id)} data-testid={`delete-route-${r.route_id}`}><Trash2 size={16} /></Button>
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="requests" className="mt-6 space-y-3">
          {received.length === 0 && <p className="text-sm text-muted-foreground">Nessuna richiesta ricevuta.</p>}
          {received.map((b) => (
            <div key={b.booking_id} className="border border-border rounded-sm p-4 bg-card" data-testid={`request-${b.booking_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-display font-semibold">{b.route_summary}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Da {b.user_name}{b.user_phone ? ` · ${b.user_phone}` : ""} · {CARGO_LABELS[b.cargo_type] || b.cargo_type}{b.weight_kg ? ` · ${b.weight_kg} kg` : ""}
                  </p>
                  {(b.pickup_stop || b.dropoff_stop) && <p className="text-xs text-muted-foreground">{b.pickup_stop} → {b.dropoff_stop}</p>}
                  {b.cargo_description && <p className="text-sm mt-1">{b.cargo_description}</p>}
                  <div className="flex flex-wrap gap-3 mt-2 text-sm">
                    {b.offered_price ? <span>Offerta: <strong>{formatEUR(b.offered_price)}</strong></span> : null}
                    {b.insurance_enabled && <span className="flex items-center gap-1 text-primary"><ShieldCheck size={14} /> Polizza {formatEUR(b.insurance_premium)} (val. {formatEUR(b.cargo_value)})</span>}
                  </div>
                </div>
                <Badge className={`rounded-sm text-white ${statusColor[b.status]}`}>{STATUS_LABELS[b.status]}</Badge>
              </div>
              {b.status === "pending" && (
                <div className="flex gap-2 mt-3">
                  <Button size="sm" className="rounded-sm bg-emerald-600 hover:bg-emerald-700" onClick={() => setStatus(b.booking_id, "accepted")} data-testid={`accept-${b.booking_id}`}>Accetta</Button>
                  <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setStatus(b.booking_id, "rejected")} data-testid={`reject-${b.booking_id}`}>Rifiuta</Button>
                </div>
              )}
              {b.status === "accepted" && (
                <Button size="sm" variant="outline" className="rounded-sm mt-3" onClick={() => setStatus(b.booking_id, "completed")} data-testid={`complete-${b.booking_id}`}>Segna come completata</Button>
              )}
            </div>
          ))}
        </TabsContent>
      </Tabs>
    </DashboardShell>
  );
}
